import { useCallback, useEffect, useState } from "react";
import { clss } from "../../../../utils/helpers/styleHelpers";
import { menuItems } from "../../data";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Span } from "../../../Text/Text";
import Icon from "../../../Icon/Icon";
import { AccessLevelE, IconColorE, UserRoleE } from "../../../../models/enums";
import { useAuth } from "./../../../../utils/hooks/useAuth";
import { filterByRole, filterPublicPages } from "./../../helpers/filterByRole";
import { useLazyGetNumberOfUnprocessedMessagesQuery } from "../../../../api/supportMessagesAPI";
import { RoutesE } from "../../../../routes/routesData";
import CountUp from "react-countup";
import { useLazyGetNotificationsAmountQuery } from "../../../../api/projectsApi";
import { getItemIdForProductTour } from "../../helpers/getItemIdForProductTour";
import Tooltip from "../../../Tooltip/Tooltip";
import LangSelect from "../../../LangSelect/LangSelect";

import styles from "./menu.module.scss";

/**
 * Top bar navigation
 */

const Menu = () => {
  const [open, setOpen] = useState(false);
  const { currentUser } = useAuth();
  const [getUnprocessedMessagesCount] =
    useLazyGetNumberOfUnprocessedMessagesQuery();
  const [getNotificationsCount] = useLazyGetNotificationsAmountQuery();
  const [unprocessedMessagesCount, setUnprocessedMessagesCount] = useState(0);
  const [tooltipText, setTooltipText] = useState("");
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      pathname.includes(RoutesE.SETTINGS) &&
      currentUser.role === UserRoleE.DeliveryService &&
      currentUser?.accessLevel !== AccessLevelE.DeliveryServicePrivate
    ) {
      navigate(RoutesE.HOME);
    }
  }, [pathname]);

  useEffect(() => {
    if (currentUser && currentUser.accessLevel === AccessLevelE.GlobalAdmin) {
      getUnprocessedMessagesCount({}).then(({ data }) => {
        setUnprocessedMessagesCount(data);
        setTooltipText("");
      });
    } else {
      if (
        currentUser?.role === UserRoleE.AdvertisingAgency ||
        currentUser?.role === UserRoleE.Broadcaster ||
        currentUser?.role === UserRoleE.GlobalAdmin
      ) {
        getNotificationsCount({}).then(
          ({ data: { VideoAdNotificationsNum } = 0 }) => {
            setUnprocessedMessagesCount(VideoAdNotificationsNum);
            setTooltipText("Unprocessed deliveries for last 3 months");
          }
        );
      }
    }
  }, [currentUser?.userName, currentUser?.role]);

  const toggleMobileMenu = () => {
    setOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setOpen(false);
    setExpandedMobileSection(-1);
  };

  const [expandedMobileSection, setExpandedMobileSection] = useState(-1);

  const changeExpandedSection = (sectionIndex: number) => {
    if (expandedMobileSection === sectionIndex) {
      setExpandedMobileSection(-1);
    } else {
      setExpandedMobileSection(sectionIndex);
    }
  };

  const Counter = useCallback(
    () =>
      !!unprocessedMessagesCount ? (
        <div className={styles.unprocessedCounter}>
          <Tooltip text={tooltipText}>
            <CountUp end={unprocessedMessagesCount} duration={1} />
          </Tooltip>
        </div>
      ) : (
        <></>
      ),
    [unprocessedMessagesCount, tooltipText]
  );

  const items = menuItems
    .filter((item) =>
      currentUser
        ? filterByRole(
            item.role as UserRoleE[],
            currentUser,
            item?.globalAdminOnly
          )
        : filterPublicPages(item)
    )
    .filter((item) =>
      (item.link === RoutesE.CAMPAIGNS || item.link === RoutesE.DELIVERIES) &&
      currentUser.role === UserRoleE.DeliveryService
        ? currentUser.accessLevel === AccessLevelE.DeliveryServicePrivate
        : true
    )
    .filter((item) =>
      item.link === RoutesE.SETTINGS &&
      currentUser?.role === UserRoleE.DeliveryService
        ? currentUser.accessLevel === AccessLevelE.DeliveryServicePrivate
        : true
    )
    .map(({ name, link, items }, index) => {
      // A parent with items may also carry a link (Reports, spec 46): the label is then a link and
      // the nested list is its sibling — anchors must not nest — inside the hoverable wrapper.
      const isLinkedParentActive =
        !!link && !!items?.some((child) => child.link === pathname);

      return (
        <li key={name}>
          {!items ? (
            // item without a nested list
            /*@ts-ignore*/
            <NavLink
              id={getItemIdForProductTour(link)}
              to={link}
              className={({ isActive }) =>
                clss([isActive ? styles.active : "", styles.navLink])
              }
              onClick={closeMobileMenu}
            >
              <Span>{name}</Span>
              {((link === RoutesE.MANAGE_LEADS &&
                currentUser?.accessLevel === AccessLevelE.GlobalAdmin) ||
                (link === RoutesE.DELIVERIES &&
                  currentUser?.accessLevel !== AccessLevelE.GlobalAdmin)) && (
                <Counter />
              )}
            </NavLink>
          ) : (
            // item with a nested list
            <div
              className={clss([
                styles.navLink,
                isLinkedParentActive ? styles.active : "",
              ])}
              // A linked parent navigates on tap (mobile) / click (desktop); only a link-less parent
              // toggles its mobile section.
              onClick={link ? undefined : () => changeExpandedSection(index)}
            >
              {link ? (
                /*@ts-ignore*/
                <NavLink
                  to={link}
                  className={styles.navLinkLabel}
                  onClick={closeMobileMenu}
                >
                  <Span>{name}</Span>
                </NavLink>
              ) : (
                <Span>{name}</Span>
              )}
              <ul
                className={clss([
                  styles.nestedNav,
                  // mobile: a linked parent's children are always listed, no tap to reveal
                  link ? styles.alwaysExpanded : "",
                  expandedMobileSection === index ? styles.expanded : "",
                ])}
              >
                {/* nested menu items */}
                <div className={styles.nestedListWrapper}>
                  {items
                    .filter((item) =>
                      filterByRole(
                        item.role as UserRoleE[],
                        currentUser,
                        item?.globalAdminOnly
                      )
                    )
                    .map(({ name: nestedName, link: nestedLink }) => (
                      <li key={nestedName}>
                        {/*@ts-ignore*/}
                        <NavLink
                          to={nestedLink}
                          className={({ isActive }) => {
                            return clss([
                              isActive ? styles.activeNested : "",
                              styles.navLinkNested,
                            ]);
                          }}
                          onClick={closeMobileMenu}
                        >
                          <Span>{nestedName}</Span>
                        </NavLink>
                      </li>
                    ))}
                </div>
              </ul>
            </div>
          )}
        </li>
      );
    });

  return (
    <>
      <nav>
        <ul className={styles.mainNav}>{items}</ul>
      </nav>

      <div className={clss([styles.hamburgerIcon, open ? styles.open : ""])}>
        <Icon
          name={open ? "close" : "burger"}
          color={IconColorE.DARK_GRAY}
          onClick={toggleMobileMenu}
        />
        <div className={styles.mobileMenuPanel}>
          <ul className={styles.mobileMenu}>
            {items}
            {/* Terms / Privacy links (mobile menu only — on desktop these live in the footer). */}
            <li>
              {/*@ts-ignore*/}
              <NavLink
                to={RoutesE.TERMS}
                className={styles.navLink}
                onClick={closeMobileMenu}
              >
                <Span>Terms of use</Span>
              </NavLink>
            </li>
            <li>
              {/*@ts-ignore*/}
              <NavLink
                to={RoutesE.PRIVACY_POLICY}
                className={styles.navLink}
                onClick={closeMobileMenu}
              >
                <Span>Privacy Policy</Span>
              </NavLink>
            </li>
          </ul>
          <div className={styles.mobileMenuLang}>
            <LangSelect selectStyles={{
              singleValue: (provided) => ({
                ...provided,
                color: "#000",
              }),
            }} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Menu;
